import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronDown } = FiIcons;

const FAQ = () => {
  const [openItems, setOpenItems] = useState({ 'left-0': true }); // First item open by default

  const toggleItem = (index) => {
    setOpenItems(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const leftColumnFAQs = [
    {
      question: "Do I really not need a credit card for the trial?",
      answer: "Correct. Just your email. We'll remind you when the trial ends. If you don't upgrade, your account simply pauses. No charges, ever."
    },
    {
      question: "What happens after my 7-day trial?",
      answer: "On day 6, we'll email you with an option to upgrade. If you don't, your trial ends on day 7. Your account stays active (view-only) so you don't lose your data."
    },
    {
      question: "Can I cancel anytime?",
      answer: "Yes. One click. No retention calls. No \"are you sure?\" screens. Just cancel and you're done."
    },
    {
      question: "Is this really unlimited posts?",
      answer: "Yes. We mean it. Post 1,000 times a month if you want (Pro and Growth plans). Starter has 150/month, which is still 3x more than most tools at this price."
    },
    {
      question: "Do you offer annual billing?",
      answer: "Yes! Save 20% by paying annually. That's $336/year instead of $420 for the Pro plan. Select annual billing at checkout."
    }
  ];

  const rightColumnFAQs = [
    {
      question: "Which platforms do you support?",
      answer: "Instagram, TikTok, Facebook (Pages & Groups), LinkedIn (Personal & Company), Twitter/X, YouTube (Community posts), Pinterest, Google Business Profile, Threads, Mastodon. More platforms added monthly."
    },
    {
      question: "Can I manage multiple brands?",
      answer: "Absolutely. Pro plan = 15 profiles (mix & match brands). Growth = 25 profiles. Each profile can be a different brand, client, or business."
    },
    {
      question: "Is there a setup fee?",
      answer: "No. Never. Setup is free and takes 5 minutes."
    },
    {
      question: "What if I need help?",
      answer: "Email support responds in under 4 hours (weekdays). Priority support for Pro/Growth gets sub-1-hour response times. Plus comprehensive video tutorials and documentation."
    },
    {
      question: "Can I upgrade or downgrade later?",
      answer: "Yes! Change plans anytime. Upgrades take effect immediately. Downgrades take effect at the next billing cycle."
    }
  ];

  const FAQColumn = ({ faqs, columnIndex }) => (
    <div className="space-y-4">
      {faqs.map((faq, index) => {
        const itemIndex = `${columnIndex}-${index}`;
        const isOpen = openItems[itemIndex];
        
        return (
          <motion.div 
            key={itemIndex}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3 }}
            className="border-b border-gray-200 last:border-0"
          >
            <button 
              onClick={() => toggleItem(itemIndex)}
              className="w-full py-6 text-left flex items-start justify-between hover:text-brand-teal transition-colors group"
            >
              <h3 className="text-lg font-bold text-brand-navy pr-4 group-hover:text-brand-teal">
                {faq.question}
              </h3>
              <div className={`transform transition-transform duration-300 mt-1 ${isOpen ? 'rotate-180' : ''}`}>
                <SafeIcon icon={FiChevronDown} className="text-brand-teal text-xl flex-shrink-0" />
              </div>
            </button>
            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="pb-6 pr-6">
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );

  return (
    <section id="faq" className="py-24 px-4 sm:px-6 lg:px-8 bg-white scroll-mt-20">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-brand-navy mb-6">
            Questions? We've Got Answers.
          </h2>
        </motion.div>

        {/* FAQ Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          <FAQColumn faqs={leftColumnFAQs} columnIndex="left" />
          <FAQColumn faqs={rightColumnFAQs} columnIndex="right" />
        </div>
      </div>
    </section>
  );
};

export default FAQ;